/**
 * Wallet state orchestration — combines CloudStorage (encrypted blob)
 * with IndexedDB (random AES key) into a single create/load/clear API.
 *
 * The data flow:
 *
 *   saveWallet(phrase)
 *     1. Generate fresh 32-byte AES key
 *     2. Encrypt phrase → keystore JSON
 *     3. Write keystore JSON → CloudStorage (syncs across TG devices)
 *     4. Write AES key → IndexedDB (this device only)
 *     5. Derive and return the ZEC transparent address (the Unified
 *        Address is populated later, in the shielded module, once the
 *        WebZjs WASM has derived the UFVK + the backend returned a UA).
 *
 *   loadWallet()
 *     1. Read keystore JSON from CloudStorage
 *     2. Read AES key from IndexedDB
 *     3. If either missing → return a status describing what's missing
 *     4. Decrypt → return phrase + addresses
 *
 *   clearWallet()
 *     Wipes both. User starts fresh.
 */

import { deriveAllAddresses, type WalletAddresses } from "../crypto/addresses";
import {
  decryptPhrase,
  encryptPhrase,
  generateEncryptionKey,
  type KeystoreFile,
} from "../crypto/keystore";
import { cloudGet, cloudRemove, cloudSet } from "./cloud";
import { idbClearKey, idbGetKey, idbSetKey } from "./indexed";

const CLOUD_KEY = "ks_v1";

export type UnlockedWallet = {
  phrase: string;
  addresses: WalletAddresses;
};

export type LoadResult =
  | { kind: "unlocked"; phrase: string; addresses: WalletAddresses }
  /** Ciphertext exists in CloudStorage but no key in IndexedDB — needs mnemonic restore */
  | { kind: "needs-restore"; reason: string }
  /** No wallet anywhere — first-time user */
  | { kind: "none" }
  /** Something went wrong — present a retry */
  | { kind: "error"; message: string };

/**
 * Encrypt + persist a wallet from a mnemonic phrase.
 * Used by both the "create new" and "import existing" flows.
 */
export const saveWallet = async (phrase: string): Promise<UnlockedWallet> => {
  const key = generateEncryptionKey();
  const keystore = await encryptPhrase(phrase, key);
  await cloudSet(CLOUD_KEY, JSON.stringify(keystore));
  await idbSetKey(key);
  return {
    phrase,
    addresses: deriveAllAddresses(phrase),
  };
};

/** Read + decrypt the stored wallet. Returns a discriminated load result. */
export const loadWallet = async (): Promise<LoadResult> => {
  let cloudBlob: string | null;
  try {
    cloudBlob = await cloudGet(CLOUD_KEY);
  } catch (e) {
    return { kind: "error", message: `CloudStorage read failed: ${(e as Error).message}` };
  }

  const aesKey = await idbGetKey();

  if (!cloudBlob && !aesKey) return { kind: "none" };

  if (!cloudBlob && aesKey) {
    // Orphan key — no ciphertext to decrypt. Clean up and treat as none.
    await idbClearKey();
    return { kind: "none" };
  }

  if (cloudBlob && !aesKey) {
    return {
      kind: "needs-restore",
      reason:
        "Your encrypted wallet is here, but the decryption key isn't on this device. " +
        "Paste your 12-word mnemonic to restore.",
    };
  }

  // Both present — decrypt.
  let keystore: KeystoreFile;
  try {
    keystore = JSON.parse(cloudBlob!) as KeystoreFile;
  } catch {
    return { kind: "error", message: "Stored wallet blob is malformed JSON." };
  }

  try {
    const phrase = await decryptPhrase(keystore, aesKey!);
    return {
      kind: "unlocked",
      phrase,
      addresses: deriveAllAddresses(phrase),
    };
  } catch (e) {
    return { kind: "error", message: (e as Error).message };
  }
};

// Per-user localStorage namespaces that carry wallet activity. We wipe
// all of these on reset so a fresh wallet doesn't inherit the previous
// user's history. The first three are exact keys; the last is a
// prefix because the shielded delta state is keyed by a hash of the
// previous UFVK.
const PER_USER_LOCAL_KEYS = [
  "telecrypt.tx-history.v1",
  "telecrypt.incoming-notes.v1",
  "telecrypt.recent-contacts.v1",
  "telecrypt.backup-state.v1",
  "telecrypt.device-warning.dismissed-at",
  "zcashwallet.telemetry.v1",
];
const PER_USER_LOCAL_PREFIXES = ["telecrypt.shielded.delta-state."];

const wipePerUserLocalStorage = (): void => {
  if (typeof localStorage === "undefined") return;
  try {
    for (const k of PER_USER_LOCAL_KEYS) localStorage.removeItem(k);
    const prefixHits: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (PER_USER_LOCAL_PREFIXES.some((p) => k.startsWith(p))) {
        prefixHits.push(k);
      }
    }
    for (const k of prefixHits) localStorage.removeItem(k);
  } catch {
    // quota / privacy mode — silent. If the wipe can't complete, the
    // worst case is the next wallet sees ghost data on Wallet/Contacts.
  }
};

/**
 * Wipe everything that ties the app to the current wallet:
 *   - encrypted keystore in TG CloudStorage
 *   - device AES key in IndexedDB
 *   - all per-user localStorage namespaces (tx history, incoming notes,
 *     recent contacts, backup-ack state, telemetry pref, shielded
 *     scanner delta state)
 *   - module-level caches in the EVM holdings hook
 *
 * Caller should confirm with the user before invoking — there is no
 * undo. The 12-word recovery phrase remains the only path back to
 * funds previously held under this wallet.
 */
export const clearWallet = async (): Promise<void> => {
  // Best-effort: each leg runs independently so a partial wipe still
  // gets us closer to clean. The next wallet's setup re-creates whatever
  // it needs.
  wipePerUserLocalStorage();
  // Fire-and-forget reset for the EVM holdings module-level cache.
  // Lazy-import to avoid a cycle (use-evm-holdings imports the wallet
  // context elsewhere in the tree).
  try {
    const mod = await import("../../evm/use-evm-holdings");
    mod.resetEvmHoldingsCache?.();
  } catch {
    /* ignore — the next mount will refetch anyway */
  }
  await Promise.all([cloudRemove(CLOUD_KEY).catch(() => {}), idbClearKey().catch(() => {})]);
};

/** Quick existence check without decrypting — for boot routing. */
export const walletExists = async (): Promise<boolean> => {
  try {
    const blob = await cloudGet(CLOUD_KEY);
    return blob != null;
  } catch {
    return false;
  }
};
